import React, { useState, useEffect, useCallback } from "react";
import { Box, Paper, Typography, TextField, Button, CircularProgress, Chip } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  usePreloadDynamicPropertyTypes,
  useUpdateDynamicPropertyType,
  type DynamicPropertyType,
} from "@/api/hooks/useDynamicPropertyTypes";

const DEFAULT_CHALLENGE_PROPERTY_NAME = "default_org_challenge";

const SystemPropertiesCard: React.FC = () => {
  const { data, isLoading } = usePreloadDynamicPropertyTypes();
  const updateMutation = useUpdateDynamicPropertyType();
  const { enqueueSnackbar } = useSnackbar();

  const [uuidValue, setUuidValue] = useState("");

  const defaultChallengeProp = data?.data?.find(
    (p: DynamicPropertyType) => p.name === DEFAULT_CHALLENGE_PROPERTY_NAME
  );
  const propId = defaultChallengeProp?.id;
  const propDefaultValue = defaultChallengeProp?.defaultValue;

  useEffect(() => {
    if (propDefaultValue !== undefined) {
      setUuidValue(propDefaultValue);
    }
  }, [propId, propDefaultValue]);

  const handleSave = useCallback(async () => {
    if (!defaultChallengeProp) return;
    try {
      await updateMutation.mutateAsync({
        id: defaultChallengeProp.id,
        name: defaultChallengeProp.name,
        dataTypeId: defaultChallengeProp.dataTypeId,
        displayName: defaultChallengeProp.displayName,
        description: defaultChallengeProp.description,
        defaultValue: uuidValue.trim(),
      });
      enqueueSnackbar("Propiedad actualizada", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al actualizar la propiedad", { variant: "error" });
    }
  }, [defaultChallengeProp, uuidValue, updateMutation, enqueueSnackbar]);

  if (isLoading) {
    return (
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }

  if (!defaultChallengeProp) {
    return null;
  }

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          Propiedades del Sistema
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configura el reto en el que se inscriben automaticamente las organizaciones recien creadas.
      </Typography>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 320px", minWidth: 250 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>
              UUID del Reto por Defecto
            </Typography>
            <Chip size="small" label="default_org_challenge" variant="outlined" />
          </Box>
          <TextField
            fullWidth
            size="small"
            value={uuidValue}
            onChange={(e) => setUuidValue(e.target.value)}
            placeholder="Pega el UUID del reto aqui..."
            error={!!uuidValue && !uuidValue.trim().match(/^[0-9a-fA-F-]{36}$/)}
            helperText={
              uuidValue && !uuidValue.trim().match(/^[0-9a-fA-F-]{36}$/)
                ? "Formato UUID invalido"
                : "Vacio = inscripcion automatica desactivada"
            }
          />
        </Box>
        <Box sx={{ pt: 2.5 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            startIcon={updateMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            Guardar
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default SystemPropertiesCard;
